import { ConfirmationDialog } from 'components/ConfirmationDialog';
import type { IContractStatusListItem } from 'components/ContractStatus';
import {
    ControlledDateField,
    ControlledMultiselectField,
    ControlledSelectField,
    ControlledTextField,
} from 'components/Form';
import type { ITabItem } from 'components/Form/ControlledTab';
import { ControlledTabs } from 'components/Form/ControlledTab';
import { TooltipWrapper } from 'components/TooltipWrapper/TooltipWrapper';
import { Viewport } from 'components/Viewport';
import { FIELD_WIDTH } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import type {
    BimContractData,
    BimContractDefaultData,
    BimContractResultsViewData,
    BimContractStatusEnum,
    BimNormalContractResultData,
    ProjectContractTypeEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { BimTotal_AlcTypeEnum as BimTotalAlcTypeEnum, ProjectStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import type { IOptionsUseIsUniqInternalNumber } from 'hooks/useIsUniqInternalNumber';
import { useIsUniqInternalNumber } from 'hooks/useIsUniqInternalNumber';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useConstructionTypesListItems } from 'pages/Project/Common/hooks/useConstructionTypesListItems';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { TabsIds } from 'pages/Project/Common/types';
import type { IConstructionTypeListItem } from 'pages/Project/Common/utils';
import { contractTabs, getProjectContractTypeTitle } from 'pages/Project/Common/utils';
import type { DeepPartial } from 'types';
import { Collapse, Grid, GridItem, IconCalendar } from 'uikit';
import { validateInterval } from 'utils';
import { requiredBaseRule } from 'utils/constants';
import { descriptionNumberAndDate } from 'utils/descriptionNumberAndDate';
import { replacerCost } from 'utils/replacerCost';

import type { GridApi } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type { Dispatch, SetStateAction } from 'react';
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { UseFormRegister, UseFormSetValue, UseFormUnregister } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';

import { useErrorTooltipsVisibility } from '../../../../Common/hooks/useErrorTooltipsVisibility';
import type { IBimContractResultPlanDataItem } from '../../../WorkOrder/types';
import { BimContractAccumulationSheet } from '../../BimContractAccumulationSheet/BimContractAccumulationSheet';
import { BimContractReadPriceForm } from '../../BimContractRead/components/BimContractReadPriceForm';
import { bimUniqContractInternalNumberGql } from '../../BimContractRequests';
import { conversionIndexInputToFlat, useBimContractFormData } from '../../hooks/useBimContractFormData';
import type { TBimContractConversionIndexInput } from '../../hooks/useBimConversionIndicesColumns';
import { conversionCoefficientsKeys } from '../../hooks/useBimConversionIndicesColumns';
import { useBimFrameContractResults } from '../../hooks/useBimFrameContractResults';
import { useBimNormalContractResults } from '../../hooks/useBimNormalContractResults';
import type { TBimContractSeasonInflationInputRow } from '../../hooks/useBimSeasonsInflationColumns';
import { getInflationRateKey } from '../../hooks/useBimSeasonsInflationColumns';
import type { TDisabledByStatusMap } from '../hooks/useIsDisabledByStatus';
import { MayBeDisabledByStatusKeys } from '../hooks/useIsDisabledByStatus';
import { coefficientsPrepare, getConstructionTypeCoefficientsKeys } from '../hooks/useSaveContract';
import { useStatusChangeConfirmationText } from '../hooks/useStatusChangeConfirmationText';
import { useTypesLimitsFormValues } from '../hooks/useTypesLimitsFormValues';

import { BimContractEditCommonForm } from './BimContractEditCommonForm';
import { BimContractEditPriceForm } from './BimContractEditPriceForm';

import Styles from './BimContractEditLimitedCosts.module.css';

interface IBimContractEditForm {
    onSubmit: (formData: any) => any;
    initialData: BimContractData | BimContractDefaultData;
    isCreate?: boolean;
    hasWorkOrders: boolean;
    hasIncompleteWorkOrders: boolean;
    hasWorkOrderChangeRequired: boolean;
    isFrameContract: boolean;
    isDisabledByStatusMap: TDisabledByStatusMap;
    contractType: ProjectContractTypeEnum;
    isFrameContractWithWorkOrders: boolean;
    isNormalContractWithCalculation: boolean;
    contractId: number;
    statusProject?: ProjectStatusEnum;
    contractCalculationStatus?: WorkOrderStatusEnum;
}

const inputWidthStyle = { width: FIELD_WIDTH };
const tabsHeightStyle = { height: 38 };
const validateSeasons = {
    validate: validateInterval,
};
const seasonDependentCollectionsTypes = [
    BimTotalAlcTypeEnum.SeasonCosts,
    BimTotalAlcTypeEnum.SeasonInflation,
    BimTotalAlcTypeEnum.SeasonWithUnforeseenCosts,
];

interface IInsertToCostGroupParams {
    groupType: BimTotalAlcTypeEnum;
    type: BimTotalAlcTypeEnum;
    years: any;
    isEditable: boolean;
    nameGetter: (contractSeasonYear: number) => string;
    api: GridApi<IBimContractResultPlanDataItem>;
    register: UseFormRegister<any>;
    unregister: UseFormUnregister<any>;
    setValue: UseFormSetValue<any>;
}

const updateSeasonGroup = (props: IInsertToCostGroupParams) => {
    const { years, type, groupType, isEditable, nameGetter, api, register, unregister, setValue } = props;

    const yearsToCheck: number[] = [];
    const rowsToRemove: IBimContractResultPlanDataItem[] = [];
    const oldValues: Record<string, number> = {};
    let groupTypeIndex = -1;
    const startYear = years[0];
    const endYear = years[years.length - 1];

    api.forEachNode((rowNode, index) => {
        if (rowNode?.data?.totalType === type) {
            rowsToRemove.push(rowNode?.data);
            yearsToCheck.push(rowNode?.data?.contractSeasonYear);
            oldValues[rowNode?.data?.id] = rowNode?.data?.contractPlan;
        }

        if (rowNode?.data?.totalType === groupType) {
            groupTypeIndex = index;
        }
    });

    if (startYear === yearsToCheck[0] && endYear === yearsToCheck[yearsToCheck.length - 1]) {
        return;
    }

    if (rowsToRemove.length) {
        rowsToRemove.forEach((row) => unregister(row?.id));
        api.applyTransaction({
            remove: rowsToRemove,
        });
    }

    const add = [];

    for (let index = 0; startYear + index <= endYear; index++) {
        const contractSeasonYear = startYear + index;
        const id = `${type}_${contractSeasonYear}`;
        const contractPlan = oldValues[id];

        register(id);
        setValue(id, contractPlan);

        add.push({
            isEditable,
            hasSeason: true,
            id,
            isHeadLine: false,
            isLimitPrice: false,
            contractSeasonYear,
            name: nameGetter(contractSeasonYear),
            contractPlan,
            totalType: type,
        });
    }

    if (groupTypeIndex !== -1) {
        api.applyTransaction({
            add,
            addIndex: groupTypeIndex + 1,
        });
    }
};

const nameGetter = (contractSeasonYear: number) => `Сезон ${contractSeasonYear}`;

export const BimContractEditForm = forwardRef((props: IBimContractEditForm, ref) => {
    const {
        onSubmit,
        initialData,
        statusProject,
        isCreate,
        hasWorkOrders,
        isDisabledByStatusMap,
        isFrameContract,
        hasIncompleteWorkOrders,
        hasWorkOrderChangeRequired,
        contractType,
        isFrameContractWithWorkOrders,
        isNormalContractWithCalculation,
        contractId,
        contractCalculationStatus,
    } = props;

    const formRef = useRef<HTMLFormElement>(null);
    const gridApiRef = useRef<AgGridReact<IBimContractResultPlanDataItem> | null>(null);
    const [activeTab, setActiveTab] = useState<ITabItem<TabsIds>>(contractTabs[isCreate ? 1 : 0]);
    const [resultPlanValues, setResultPlanValues] = useState<IBimContractResultPlanDataItem[]>([]);
    const [frameContractResults, setFrameContractResults] = useState<BimContractResultsViewData | undefined | null>();
    const [normalContractResults, setNormalContractResults] = useState<
        (BimNormalContractResultData | null)[] | undefined | null
    >();
    const [isOpen, setOpen] = useState<boolean>(true);
    const { isHeaderOpened: isContextHeaderOpen } = useContext(PageHeaderLayoutContext);

    const { contractBim: contractBimRules } = useAccess();
    const { constructionTypesListItems } = useConstructionTypesListItems();

    const defaultValues = useBimContractFormData(initialData);

    const { bimFrameContractResultsStatus, bimFrameContractResultsRequest } = useBimFrameContractResults(
        contractId,
        setFrameContractResults as Dispatch<SetStateAction<DeepPartial<BimContractResultsViewData> | undefined | null>>,
    );

    const { bimNormalContractResultsStatus, bimNormalContractResultsRequest } = useBimNormalContractResults(
        contractId,
        setNormalContractResults,
    );

    const isAccumulationSheetLoading = useMemo(
        () =>
            bimFrameContractResultsStatus === RequestStatus.FETCHING ||
            bimNormalContractResultsStatus === RequestStatus.FETCHING,
        [bimFrameContractResultsStatus, bimNormalContractResultsStatus],
    );

    const {
        control,
        handleSubmit,
        formState,
        clearErrors,
        watch,
        setValue,
        getValues,
        setError,
        register,
        unregister,
    } = useForm<any>({
        criteriaMode: 'all',
        shouldUnregister: true,
        defaultValues,
    });

    const seasonsRangeField = useWatch({
        control,
        name: 'seasonsRange',
    });

    const internalNumber = useWatch({
        control,
        name: MayBeDisabledByStatusKeys.generalInfoInternalNumber,
    });

    const signingDate = useWatch({
        control,
        name: MayBeDisabledByStatusKeys.generalInfoSigningDate,
    });

    const constructionTypesFields = useWatch({ control, name: 'constructionTypes' });

    const [currentStatus, setCurrentStatus] = useState(initialData?.status);

    const currentFormStatus = useWatch({ control, name: 'status' });

    const contractTitle = useMemo(
        () => `Договор${descriptionNumberAndDate(internalNumber, signingDate)}`,
        [internalNumber, signingDate],
    );

    const conversionIndicesItems: TBimContractConversionIndexInput[] = useMemo(
        () =>
            constructionTypesFields?.reduce(
                (acc: TBimContractConversionIndexInput[], type: IConstructionTypeListItem) => {
                    const { id } = type;
                    const coefficients = coefficientsPrepare(getValues() || {}, type.id);

                    acc = [
                        ...acc,
                        {
                            id: String(id),
                            coefficients,
                            constructionTypeId: type.id,
                        },
                    ];

                    return acc;
                },
                [],
            ) || [],
        [constructionTypesFields, getValues],
    );

    const seasonsInflationValues = useMemo(() => {
        const years = seasonsRangeField?.map((date?: Date) => (date ? date?.getFullYear() : undefined));

        const inflationInputs: TBimContractSeasonInflationInputRow[] = [];

        if (years?.length) {
            const startYear = years[0];
            const endYear = years[years.length - 1];

            for (let index = 0; startYear + index <= endYear; index++) {
                const contractSeasonYear = startYear + index;
                const currentInflation = getValues()?.[getInflationRateKey({ contractSeasonYear } as any)];

                inflationInputs.push({
                    id: contractSeasonYear,
                    contractSeasonYear,
                    inflationRate: currentInflation,
                });
            }
        }

        return inflationInputs;
    }, [getValues, seasonsRangeField]);

    const seasonInflationFieldsNames = useMemo(
        () => (seasonsInflationValues || []).map((inf) => getInflationRateKey(inf)),
        [seasonsInflationValues],
    );
    const conversionIndicesFieldsNames = useMemo(
        () =>
            (conversionIndicesItems || []).reduce(
                (acc: string[], ci) => [...acc, ...getConstructionTypeCoefficientsKeys(ci.constructionTypeId)],
                [],
            ),
        [conversionIndicesItems],
    );

    const typesLimitsObj = useTypesLimitsFormValues(control);

    const limitedCostsFieldsToCheck = useMemo(
        () =>
            constructionTypesFields?.reduce(
                (acc: any[], ct: IConstructionTypeListItem) => [
                    ...acc,
                    `limitedCosts.regionalFactor_${ct.id}`,
                    `limitedCosts.typesLimitedCost_${ct.id}`,
                    ...(typesLimitsObj?.[ct.id]?.map(
                        (limitType) => `limitedCosts.rateValueLimitedCost_${ct.id}_${limitType.type}`,
                    ) || []),
                ],
                [],
            ) || [],
        [constructionTypesFields, typesLimitsObj],
    );

    const fieldsToCheckLists = useMemo(
        () => ({
            [TabsIds.COMMON]: ['generalInfo.subject', 'generalInfo.internalNumber', 'generalInfo.contractor'],
            [TabsIds.PRICE]: [
                'supplyBasis',
                ...seasonInflationFieldsNames,
                ...conversionIndicesFieldsNames,
                ...limitedCostsFieldsToCheck,
            ],
            [TabsIds.ACCUMULATION_SHEET]: [],
        }),
        [conversionIndicesFieldsNames, limitedCostsFieldsToCheck, seasonInflationFieldsNames],
    );

    const priceCollapsesFieldsToCheck = useMemo(
        () => [
            {
                index: 0,
                fields: conversionIndicesFieldsNames,
                errorMessage: 'Введите хотя бы один положительный индекс для каждого вида строительства',
            },
            {
                index: 1,
                fields: seasonInflationFieldsNames,
                errorMessage: 'Поля обязательны для заполнения',
            },
        ],
        [conversionIndicesFieldsNames, seasonInflationFieldsNames],
    );

    const onTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    const { classNameTooltips, onTooltipAttributeChange } = useErrorTooltipsVisibility({ activeTab, formRef });

    const optionsUniqInternalNumber: IOptionsUseIsUniqInternalNumber = useMemo(
        () => ({
            idData: { name: 'contractId', value: defaultValues?.id },
            request: { name: 'bimIsUniqueContractInternalNumber', value: bimUniqContractInternalNumberGql },
            changedValue: { name: 'internalNumber', value: internalNumber },
        }),
        [internalNumber, defaultValues?.id],
    );
    const isUniqInternalNumber = useIsUniqInternalNumber(optionsUniqInternalNumber);

    useEffect(() => {
        if (internalNumber && defaultValues.generalInfo.internalNumber !== internalNumber && !isUniqInternalNumber) {
            setError('generalInfo.internalNumber', { message: 'Проект с таким внутренним номером уже существует' });
        } else {
            clearErrors('generalInfo.internalNumber');
        }
    }, [
        clearErrors,
        internalNumber,
        defaultValues.internalNumber,
        isUniqInternalNumber,
        setError,
        defaultValues.generalInfo.internalNumber,
    ]);

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        },
        isDirty: formState.isDirty,
    }));

    useEffect(() => {
        seasonsInflationValues.forEach((value) => {
            const { inflationRate } = value;

            setValue(getInflationRateKey(value), inflationRate);
        });
    }, [seasonsInflationValues, setValue]);

    useEffect(() => {
        conversionIndicesItems.forEach((rowItem) => {
            Object.entries(conversionIndexInputToFlat(rowItem)).forEach(([key, value]) => {
                setValue(key, value);
            });
        });
    }, [conversionIndicesItems, setValue]);

    useEffect(() => {
        clearErrors(conversionIndicesFieldsNames);
        clearErrors(seasonInflationFieldsNames);
    }, [clearErrors, constructionTypesFields, conversionIndicesFieldsNames, seasonInflationFieldsNames]);

    const checkConversionCoefficientsByConstructionType = useCallback(
        (constructionTypeId: number) => {
            const data = getValues();
            const keysByType = getConstructionTypeCoefficientsKeys(constructionTypeId);
            const withValue = keysByType.some((key) => replacerCost(data[key]) > 0);

            if (!withValue) {
                keysByType.forEach((key) => setError(key, { message: '' }));

                return true;
            }

            keysByType.forEach((key) => clearErrors(key));

            return false;
        },
        [clearErrors, getValues, setError],
    );

    const onInvalidSubmit = useCallback(() => {
        constructionTypesFields?.some((ct: IConstructionTypeListItem) =>
            checkConversionCoefficientsByConstructionType(ct.id),
        );
    }, [checkConversionCoefficientsByConstructionType, constructionTypesFields]);

    const onLocalSubmit = useCallback(
        (formData: any) => {
            const hasErrors = constructionTypesFields?.some((ct: IConstructionTypeListItem) =>
                checkConversionCoefficientsByConstructionType(ct.id),
            );

            if (!hasErrors) {
                onSubmit(formData);
            }
        },
        [checkConversionCoefficientsByConstructionType, constructionTypesFields, onSubmit],
    );

    const { textConfirmation, confirmButtonText, cancelButtonText, confirmationOnly } = useStatusChangeConfirmationText(
        {
            status: currentFormStatus?.id,
            isFrameContract,
            hasWorkOrders,
            hasWorkOrderChangeRequired,
            hasIncompleteWorkOrders,
        },
    );

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: () => false,
        });

    const onCloseStatusChange = useCallback(
        (isApproved: boolean) => {
            if (!isApproved || confirmationOnly) {
                setValue(
                    'status',
                    (defaultValues?.statusFollowers || []).find(
                        (s: { id: BimContractStatusEnum | undefined }) => s.id === currentStatus,
                    ),
                );
            } else {
                setCurrentStatus(currentFormStatus?.id);
            }

            handleCloseConfirmationDialog();
        },
        [
            confirmationOnly,
            currentFormStatus?.id,
            currentStatus,
            defaultValues?.statusFollowers,
            handleCloseConfirmationDialog,
            setValue,
        ],
    );

    useEffect(() => {
        if (currentStatus && currentFormStatus?.id !== currentStatus && textConfirmation) {
            handleOpenConfirmationDialog();
        }
    }, [currentFormStatus?.id, currentStatus, handleOpenConfirmationDialog, textConfirmation]);

    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (name && type === 'change') {
                const coefficient = conversionCoefficientsKeys.find((k) => name.startsWith(k));

                if (coefficient) {
                    const constructionTypeId = Number(name.replace(/[^0-9-]+/g, ''));

                    if (constructionTypeId) {
                        checkConversionCoefficientsByConstructionType(constructionTypeId);
                    }
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [
        checkConversionCoefficientsByConstructionType,
        clearErrors,
        getValues,
        handleOpenConfirmationDialog,
        setError,
        setValue,
        textConfirmation,
        watch,
    ]);

    useEffect(() => {
        const result = (defaultValues?.resultPlan as IBimContractResultPlanDataItem[])?.filter(
            (item) =>
                !seasonDependentCollectionsTypes.includes(item.totalType as BimTotalAlcTypeEnum) ||
                item.contractSeasonYear,
        );

        result.forEach((item) => {
            if (item.isEditable) {
                item?.constructionTypePlanResults?.forEach((plan) => {
                    const idByType = `contractPlan_${item.id}_${plan.constructionTypeId}`;

                    register(idByType);
                    setValue(idByType, plan.contractPlan);
                });
            }
        }, []);

        setResultPlanValues(result);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValues?.resultPlan]);

    useEffect(() => {
        const years = seasonsRangeField?.map((date?: Date) => (date ? date?.getFullYear() : undefined));

        if (years?.length && gridApiRef?.current?.api) {
            updateSeasonGroup({
                groupType: BimTotalAlcTypeEnum.SeasonCostsGroup,
                type: BimTotalAlcTypeEnum.SeasonCosts,
                years,
                isEditable: true,
                nameGetter,
                register,
                unregister,
                setValue,
                api: gridApiRef?.current?.api,
            });

            updateSeasonGroup({
                groupType: BimTotalAlcTypeEnum.SeasonInflationGroup,
                type: BimTotalAlcTypeEnum.SeasonInflation,
                years,
                isEditable: false,
                nameGetter: (contractSeasonYear: number) => `Размер инфляции на ${contractSeasonYear} год`,
                register,
                unregister,
                setValue,
                api: gridApiRef?.current?.api,
            });

            updateSeasonGroup({
                groupType: BimTotalAlcTypeEnum.SeasonWithUnforeseenCostsGroup,
                type: BimTotalAlcTypeEnum.SeasonWithUnforeseenCosts,
                years,
                isEditable: true,
                nameGetter,
                register,
                unregister,
                setValue,
                api: gridApiRef?.current?.api,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seasonsRangeField]);

    useEffect(() => {
        if (activeTab?.id === TabsIds.ACCUMULATION_SHEET) {
            if (isFrameContractWithWorkOrders && !frameContractResults) {
                bimFrameContractResultsRequest();
            }

            if (isNormalContractWithCalculation && !normalContractResults) {
                bimNormalContractResultsRequest();
            }
        }
    }, [
        activeTab?.id,
        bimFrameContractResultsRequest,
        bimNormalContractResultsRequest,
        frameContractResults,
        isFrameContractWithWorkOrders,
        isNormalContractWithCalculation,
        normalContractResults,
    ]);

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;

    const statusItems = useMemo(() => {
        const { statusFollowers = [], status } = defaultValues || {};

        const defaultStatusesExcludeCurrent = status
            ? statusFollowers?.filter((s: IContractStatusListItem) => s.id !== status.id)
            : statusFollowers;

        return [...defaultStatusesExcludeCurrent, ...(status ? [status] : [])];
    }, [defaultValues]);

    const topIndention = useMemo(() => {
        if (!isContextHeaderOpen && !isOpen) {
            return '-214px';
        }

        const heightContext = !isContextHeaderOpen ? 160 : 0;

        return isOpen ? `${200 - heightContext}px` : `${0 - heightContext}px`;
    }, [isContextHeaderOpen, isOpen]);

    return (
        <>
            <form onSubmit={handleSubmit(onLocalSubmit, onInvalidSubmit)} ref={formRef}>
                <Collapse label="Основная информация" isOpen={isOpen} onClick={() => setOpen(!isOpen)}>
                    <Grid rowGap="s" cols="1">
                        <GridItem style={inputWidthStyle}>
                            <ControlledSelectField
                                name="status"
                                label="Статус"
                                control={control}
                                placeholder="Статус"
                                items={statusItems}
                                disabled={!contractBimRules.canCreate || isProjectStatusDelayed}
                            />
                        </GridItem>
                        <GridItem style={inputWidthStyle}>
                            <ControlledTextField
                                name="authoredBy"
                                label="Автор"
                                control={control}
                                type="text"
                                placeholder="Автор"
                                disabled
                            />
                        </GridItem>
                        <GridItem style={inputWidthStyle}>
                            <ControlledTextField
                                name="contractType"
                                label="Тип договора"
                                control={control}
                                type="text"
                                placeholder={getProjectContractTypeTitle(contractType)}
                                disabled
                            />
                        </GridItem>
                        <GridItem style={inputWidthStyle}>
                            <ControlledMultiselectField
                                label="Виды строительства"
                                name={MayBeDisabledByStatusKeys.constructionTypes}
                                control={control}
                                labelPosition="left"
                                items={constructionTypesListItems}
                                required
                                rules={requiredBaseRule}
                                disabled={
                                    isDisabledByStatusMap[MayBeDisabledByStatusKeys.constructionTypes] ||
                                    hasWorkOrders ||
                                    !contractBimRules.canCreate ||
                                    isProjectStatusDelayed
                                }
                                disabledIds={defaultValues.usedInObjectStructure}
                                disabledItemOnHoverMessage="Имеются объекты по виду строительства на странице 'Структура договора'. Удаление недоступно"
                                hideRemoveIfDisabled
                            />
                        </GridItem>
                        <GridItem style={inputWidthStyle}>
                            <TooltipWrapper
                                message="Имеются объекты на странице 'Структура Договора'. Редактирование недоступно"
                                isHidden={!defaultValues.hasObjectStructure}
                                positionElementClassName="DatePickerMixRangeField-Body"
                            >
                                <ControlledDateField
                                    control={control}
                                    label="Сезон"
                                    name={MayBeDisabledByStatusKeys.seasonsRange}
                                    type="year-range"
                                    required
                                    rightSide={[IconCalendar, IconCalendar]}
                                    rules={validateSeasons}
                                    disabled={
                                        isDisabledByStatusMap[MayBeDisabledByStatusKeys.seasonsRange] ||
                                        defaultValues.hasObjectStructure ||
                                        !contractBimRules.canCreate ||
                                        isProjectStatusDelayed
                                    }
                                />
                            </TooltipWrapper>
                        </GridItem>
                    </Grid>
                </Collapse>
                <GridItem style={tabsHeightStyle}>
                    <ControlledTabs
                        value={activeTab}
                        onChange={onTabChange}
                        items={contractTabs}
                        view="bordered"
                        size="s"
                        fieldsToCheck={fieldsToCheckLists}
                        control={control}
                    />
                </GridItem>

                <Viewport
                    marginBottom="var(--space-margin-bottom)"
                    shownChildIndex={activeTab.id}
                    scrollTopOnIndexChange
                    scrollTopValue={5}
                    className="viewport-container"
                    topIndention={topIndention}
                >
                    <BimContractEditCommonForm
                        control={control}
                        classNameTooltip={classNameTooltips[TabsIds.COMMON]}
                        setValue={setValue}
                        watch={watch}
                        hasWorkOrders={hasWorkOrders}
                        isDisabledByStatusMap={isDisabledByStatusMap}
                        onTooltipAttributeChange={onTooltipAttributeChange}
                        isDisabledRole={!contractBimRules.canCreate}
                        statusProject={statusProject}
                    />
                    {!hasWorkOrders && !isDisabledByStatusMap[MayBeDisabledByStatusKeys.priceForm] && (
                        <BimContractEditPriceForm
                            control={control}
                            defaultValues={defaultValues}
                            seasonsInflationItems={seasonsInflationValues}
                            conversionIndicesItems={conversionIndicesItems}
                            classNameTooltip={`${Styles.customTooltip} ${classNameTooltips[TabsIds.PRICE]}`}
                            fieldsToCheck={priceCollapsesFieldsToCheck}
                            onTooltipAttributeChange={onTooltipAttributeChange}
                        />
                    )}
                    {(hasWorkOrders || isDisabledByStatusMap[MayBeDisabledByStatusKeys.priceForm]) && (
                        <BimContractReadPriceForm data={initialData as BimContractData} />
                    )}
                    <BimContractAccumulationSheet
                        constructionTypes={constructionTypesFields}
                        setValue={setValue}
                        resultPlan={resultPlanValues}
                        contractTitle={contractTitle}
                        gridApiRef={gridApiRef}
                        isReadOnly={!isFrameContract}
                        frameContractResults={frameContractResults}
                        normalContractResults={normalContractResults}
                        isLoading={isAccumulationSheetLoading}
                        statusProject={statusProject}
                        hasNoPlanByConstructionType={(initialData as BimContractData)?.hasNoPlanByConstructionType}
                        contractCalculationStatus={contractCalculationStatus}
                    />
                </Viewport>
            </form>
            <ConfirmationDialog
                textHeader="Изменение статуса"
                textConfirmation={textConfirmation}
                confirmButtonText={confirmButtonText}
                cancelButtonText={cancelButtonText}
                onSubmit={handleActionQuery}
                isOpened={isOpenConfirmationDialog}
                onClose={onCloseStatusChange}
                confirmationOnly={confirmationOnly}
            />
        </>
    );
});

BimContractEditForm.displayName = 'BimContractEditForm';
